<template>
  <b-modal
    v-model="show"
    size="md"
    :title="'Notes - ' + (dateDisplay || '')"
    centered
    ok-only
    ok-title="Close"
  >
    <div v-if="notesLoading" class="text-center py-2">
      <b-spinner small type="grow" class="mr-50" /> Loading notes...
    </div>

    <div v-else>
      <div v-if="dayNotes.length === 0" class="text-muted mb-1">
        No notes for this day yet.
      </div>

      <ul class="list-unstyled mb-1" v-else>
        <li v-for="n in dayNotes" :key="n.id" class="mb-50">
          <div class="d-flex align-items-start">
            <div class="flex-grow-1">
              <div class="font-weight-bold">
                {{ getCreatorName(n) }}
                <small class="text-muted">
                  <template v-if="n.created_at"> • {{ formatUkDateTime(n.created_at) }}</template>
                </small>
              </div>

              <div v-if="editingNoteId === n.id">
                <b-form-textarea
                  v-model="editDraftText"
                  rows="3"
                  placeholder="Edit note..."
                />

                <b-form-group label="Sites (leaving empty will select all)" class="mt-50">
                  <b-form-select
                    v-model="editDraftSites"
                    :options="siteSelectOptions"
                    multiple
                    :select-size="Math.min(6, siteSelectOptions.length || 1)"
                  />
                  <small class="text-muted" v-if="siteSelectOptions.length === 0">No sites available.</small>
                </b-form-group>
                <div class="d-flex justify-content-end mt-50">
                  <b-button size="sm" variant="outline-secondary" class="mr-50" @click="cancelEdit">
                    Cancel
                  </b-button>
                  <b-button
                    size="sm"
                    variant="primary"
                    :disabled="editDraftText.trim().length === 0 || savingEdit"
                    @click="saveEdit(n)"
                  >
                    <b-spinner v-if="savingEdit" small class="mr-50" />
                    Save
                  </b-button>
                </div>
              </div>

              <div v-else>
                <div>{{ n.note }}</div>
              </div>
            </div>

            <div class="ml-1" v-if="editingNoteId !== n.id">
              <b-button size="sm" variant="outline-primary" class="mr-50" @click="startEdit(n)">Edit</b-button>
              <b-button size="sm" variant="outline-danger" @click="deleteNote(n)">Delete</b-button>
            </div>
          </div>
        </li>
      </ul>

      <b-form-group label="Add a new note" label-for="new-note-textarea">
        <b-form-textarea
          id="new-note-textarea"
          v-model="draftText"
          rows="3"
          placeholder="Type your note..."
        />
      </b-form-group>

      <b-form-group label="Sites (leaving empty will select all, can be multiple using CTRL or CMD click)">
        <b-form-select
          v-model="draftSites"
          :options="siteSelectOptions"
          multiple
          :select-size="Math.min(6, siteSelectOptions.length || 1)"
        />
        <small class="text-muted" v-if="siteSelectOptions.length === 0">No sites available.</small>
      </b-form-group>

      <div class="d-flex justify-content-end">
        <b-button :disabled="draftText.trim().length === 0 || creatingNote" variant="primary" @click="addNote">
          <b-spinner v-if="creatingNote" small class="mr-50" />
          Add Note
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton, BFormGroup, BFormTextarea, BFormSelect, BSpinner } from "bootstrap-vue";
import moment from "moment";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "NotesModal",
  components: {
    BModal,
    BButton,
    BFormGroup,
    BFormTextarea,
    BFormSelect,
    BSpinner,
  },
  data() {
    return {
      show: false,
      dateDisplay: "",
      dateApi: "",
      selectedSites: [],
      sitesOptions: [],
      dayNotes: [],
      notesLoading: false,
      creatingNote: false,
      draftText: "",
      draftSites: [],

      editingNoteId: null,
      editDraftText: "",
      editDraftSites: [],
      savingEdit: false,

      deletingNoteId: null,
    };
  },
  computed: {
    siteSelectOptions() {
      const list = Array.isArray(this.sitesOptions) ? this.sitesOptions : [];
      return list
        .map((s) => ({ value: s.id, text: s.title }))
        .filter((o) => o.value !== undefined && o.value !== null);
    },
  },
  methods: {
    formatUkDate(value) {
      if (!value) return "";

      const str = value.toString();
      const hasZone = str.includes("Z") || /[+-]\d\d:\d\d$/.test(str);
      const hasTime = str.includes("T");

      let m;
      if (moment.tz) {
        if (hasTime && hasZone) m = moment.utc(str).tz("Europe/London");
        else if (hasTime) m = moment.tz(str, "Europe/London");
        else m = moment.tz(str, "YYYY-MM-DD", "Europe/London");
      } else {
        m = moment(str);
      }

      return m.isValid() ? m.format("DD/MM/YYYY") : str;
    },

    formatUkDateTime(value) {
      if (!value) return "";

      const str = value.toString();
      const hasZone = str.includes("Z") || /[+-]\d\d:\d\d$/.test(str);

      let m;
      if (moment.tz) {
        m = hasZone ? moment.utc(str).tz("Europe/London") : moment.tz(str, "Europe/London");
      } else {
        m = moment(str);
      }

      return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : str;
    },

    getCreatorName(note) {
      const creator = note && note.creator ? note.creator : null;
      if (!creator) return "User";

      if (creator.fullName) return creator.fullName;

      const first = creator.first_name || creator.name || "";
      const last = creator.last_name || creator.surname || "";
      const combined = `${first} ${last}`.trim();
      if (combined.length > 0) return combined;

      return creator.email || "User";
    },

    open({ dateDisplay, dateApi, selectedSites, sitesOptions }) {
      this.dateDisplay = dateDisplay || "";
      this.dateApi = dateApi || "";
      this.selectedSites = Array.isArray(selectedSites) ? selectedSites : [];
      this.sitesOptions = Array.isArray(sitesOptions) ? sitesOptions : [];
      this.dayNotes = [];
      this.draftText = "";
      this.draftSites = this.selectedSites.map((s) => parseInt(s));
      this.editingNoteId = null;
      this.editDraftText = "";
      this.editDraftSites = [];
      this.savingEdit = false;
      this.show = true;

      if (this.dateApi) {
        this.fetchNotesForDate(this.dateApi);
      }
    },

    showToast(title, text, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon,
          variant,
        },
      });
    },

    handleFetchError(error, action) {
      // Keep this lightweight; the whole point is to avoid lag.
      // Still surface a toast for the user.
      // eslint-disable-next-line no-console
      console.error(`Error during ${action}:`, error);
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "An error occurred",
          text: `Please try again later or contact support. Error: ${error.message}`,
          icon: "AlertCircleIcon",
          variant: "danger",
        },
      });
    },

    async fetchNotesForDate(dateApi) {
      this.notesLoading = true;
      try {
        const params = { date_from: dateApi, date_to: dateApi };
        if (this.selectedSites && this.selectedSites.length === 1) {
          params.site_id = parseInt(this.selectedSites[0]);
        }

        const res = await store.dispatch("dashboard/getNotes", params);
        this.dayNotes = Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        this.handleFetchError(error, "getNotes");
      } finally {
        this.notesLoading = false;
      }
    },

    startEdit(note) {
      this.editingNoteId = note.id;
      this.editDraftText = (note.note || "").toString();
      this.editDraftSites = Array.isArray(note.sites) ? note.sites.map((s) => parseInt(s)) : [];
    },

    cancelEdit() {
      this.editingNoteId = null;
      this.editDraftText = "";
      this.editDraftSites = [];
      this.savingEdit = false;
    },

    async deleteNote(note) {
      if (!note || !note.id) return;
      if (this.deletingNoteId) return;

      const ok = window.confirm("Delete this note? This action cannot be undone.");
      if (!ok) return;

      this.deletingNoteId = note.id;
      try {
        await store.dispatch("dashboard/deleteNote", note.id);

        const idx = this.dayNotes.findIndex((x) => x.id === note.id);
        if (idx !== -1) {
          this.dayNotes.splice(idx, 1);
        }

        if (this.editingNoteId === note.id) {
          this.cancelEdit();
        }

        this.showToast("Note deleted", "The note has been deleted.", "Trash2Icon", "success");
      } catch (error) {
        this.handleFetchError(error, "deleteNote");
      } finally {
        this.deletingNoteId = null;
      }
    },

    async saveEdit(note) {
      if (!note || !note.id) return;
      if (!this.editDraftText || this.editDraftText.trim().length === 0) return;

      this.savingEdit = true;
      try {
        const res = await store.dispatch("dashboard/updateNote", {
          id: note.id,
          data: {
            note: this.editDraftText.trim(),
            sites: Array.isArray(this.editDraftSites) ? this.editDraftSites.map((s) => parseInt(s)) : [],
          },
        });

        const updated = res && res.data ? res.data : null;
        const idx = this.dayNotes.findIndex((x) => x.id === note.id);
        if (idx !== -1) {
          this.$set(this.dayNotes, idx, updated || { ...this.dayNotes[idx], note: this.editDraftText.trim() });
        }

        this.cancelEdit();
        this.showToast("Note updated", "Your note has been updated.", "CheckIcon", "success");
      } catch (error) {
        this.handleFetchError(error, "updateNote");
      } finally {
        this.savingEdit = false;
      }
    },

    async addNote() {
      if (!this.draftText || this.draftText.trim().length === 0) return;
      if (!this.dateApi) return;

      this.creatingNote = true;
      try {
        const payload = {
          date: moment(this.dateApi, "YYYY-MM-DD").format("YYYY-MM-DD"),
          note: this.draftText.trim(),
        };

        payload.sites = Array.isArray(this.draftSites) ? this.draftSites.map((s) => parseInt(s)) : [];

        const res = await store.dispatch("dashboard/createNote", payload);
        if (res && res.data) {
          this.dayNotes.unshift(res.data);
          this.draftText = "";
          this.showToast("Note added", "Your note has been saved.", "CheckIcon", "success");
        }
      } catch (error) {
        this.handleFetchError(error, "createNote");
      } finally {
        this.creatingNote = false;
      }
    },
  },
};
</script>
